import { useState, useMemo } from 'react';
import { Appointment } from '../services/patientApi';
import { AppointmentsTable } from '../components/AppointmentsTable';
import { NextAppointmentCard } from '../components/NextAppointmentCard';
import { LastVisitCard } from '../components/LastVisitCard';
import { PortalSpinner } from '../components/PortalSpinner';
import { APPOINTMENT_STATUS } from '../constants';
import { useAppointmentsQuery, useConfirmAppointmentMutation } from '../queries/appointments';

type Tab = 'upcoming' | 'history';

export const Appointments = () => {
  const [activeTab, setActiveTab] = useState<Tab>('upcoming');
  const { data: appointments = [], isLoading: loading, isError: loadFailed } = useAppointmentsQuery();

  const { upcoming, history } = useMemo(() => {
    const upcoming: Appointment[] = [];
    const history: Appointment[] = [];
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    for (const appt of appointments) {
      const apptDate = new Date(appt.date);
      if (apptDate < startOfToday) {
        history.push(appt);
      } else {
        upcoming.push(appt);
      }
    }
    // Sort by full timestamp so same-day appointments come back in real time order.
    const timeOf = (a: Appointment) =>
      a.appointmentAt ? new Date(a.appointmentAt).getTime() : 0;
    upcoming.sort((a, b) => timeOf(a) - timeOf(b));
    history.sort((a, b) => timeOf(b) - timeOf(a));
    return { upcoming, history };
  }, [appointments]);

  const rows = activeTab === 'upcoming' ? upcoming : history;

  // Cards above the tabs:
  // - Next Appointment: scheduled AND not yet ended (start + duration > now).
  //   Keeps the card visible during the appointment so "Happening now" can fire.
  //   Hides it once the duration has elapsed even if backend status hasn't flipped.
  //   `upcoming` is sorted by appointmentAt asc, so .find returns the soonest.
  // - Last Visit only surfaces actually-completed visits — cancelled history
  //   rows aren't a "visit".
  const now = Date.now();
  const nextUpcoming = upcoming.find((a) => {
    if (a.status !== APPOINTMENT_STATUS.SCHEDULED) return false;
    if (!a.appointmentAt) return false;
    const startMs = new Date(a.appointmentAt).getTime();
    const endMs = startMs + (a.durationMinutes || 0) * 60_000;
    return endMs > now;
  });
  const mostRecentPast = history.find((a) => a.status === APPOINTMENT_STATUS.COMPLETED);

  // `variables` holds the id of the in-flight mutation, but TanStack Query
  // does NOT reset variables on error — only `isPending` flips back to false.
  // Gate on `isPending` so the row's "Sending request…" state clears after a failure.
  const { mutate: handleConfirm, variables, isPending } = useConfirmAppointmentMutation();
  const confirmingId = isPending ? variables ?? null : null;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-secondary-900">My Appointments</h1>
        <p className="text-sm text-secondary-500 mt-1">View your upcoming and past appointments.</p>
      </div>

      {/* Both cards render once the initial fetch completes — each handles
          its own empty state when there's no matching appointment. */}
      {!loading && !loadFailed && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <NextAppointmentCard
            appointment={nextUpcoming ?? null}
            confirmingId={confirmingId}
            onConfirm={handleConfirm}
          />
          <LastVisitCard appointment={mostRecentPast ?? null} />
        </div>
      )}

      {/* Tabs */}
      <div role="tablist" className="flex items-center gap-6 border-b border-gray-200">
        <button
          role="tab"
          aria-selected={activeTab === 'upcoming'}
          onClick={() => setActiveTab('upcoming')}
          className={`text-sm font-semibold pb-3 border-b-2 transition-colors ${
            activeTab === 'upcoming'
              ? 'text-secondary-900 border-secondary-900'
              : 'text-secondary-400 border-transparent hover:text-secondary-600'
          }`}
        >
          Upcoming{upcoming.length > 0 && ` (${upcoming.length})`}
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'history'}
          onClick={() => setActiveTab('history')}
          className={`text-sm font-semibold pb-3 border-b-2 transition-colors ${
            activeTab === 'history'
              ? 'text-secondary-900 border-secondary-900'
              : 'text-secondary-400 border-transparent hover:text-secondary-600'
          }`}
        >
          History{history.length > 0 && ` (${history.length})`}
        </button>
      </div>

      {/* Table */}
      <div className="overflow-hidden">
        {loading ? (
          <PortalSpinner />
        ) : loadFailed ? (
          <div className="py-12 text-center text-sm text-secondary-400">
            Couldn't load appointments. Please try again.
          </div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-sm text-secondary-400">
            No {activeTab} appointments.
          </div>
        ) : (
          <AppointmentsTable rows={rows} confirmingId={confirmingId} onConfirm={handleConfirm} />
        )}
      </div>
    </div>
  );
};
